import { useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { com } from '@eidu/form'
import PageLayout from '../shared/PageLayout'
import { KindViewPageProps } from '../KindPages'
import useHasPermission from '../../../permission/useHasPermission'
import { getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import useResource from '../../../../io/useResource'
import useRuntimeEntity from '../../../../io/useRuntimeEntity'
import { AssetSourceView } from './AssetSourceView'
import { AssetPreviewView } from './AssetPreviewView'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PersistentTabs, { PersistentTab } from '../../../../components/layout/PersistentTabs'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntityFieldDisplayGroup from '../shared/EntityFieldDisplayGroup'

type AssetViewTab = 'preview' | 'source' | 'fields'

const AssetViewPage = ({ entity, entityType, entityTypes, fields }: KindViewPageProps) => {
  const navigate = useNavigate()

  const { hasPermission: hasUpdatePermission } = useHasPermission(entity.entity.id, EntityAction.UpdateEntity)

  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/edit/${entity.entity.id.toString()}`)
  }

  const { content, isLoading } = useResource(getResourceKeyFromEntity(entity))
  const { runtimeEntityId } = useRuntimeEntity(entity)
  const hasRuntime = !!runtimeEntityId

  const [validationErrors, setValidationErrors] = useState<string[]>([])

  return (
    <PageLayout
      title={entity.primaryLabelText}
      subtitle={entityType.name}
      actions={hasUpdatePermission ? <Button onClick={navigateToEdit}>Edit</Button> : undefined}
      showBack
    >
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <PersistentTabs<AssetViewTab> defaultValue={hasRuntime ? 'preview' : 'source'} sx={{ mb: 1, flexShrink: 0 }}>
          {hasRuntime && (
            <PersistentTab<AssetViewTab> label="Preview" value="preview">
              <AssetPreviewView
                entity={entity}
                entityKind={getKnownKindKey(entityType.kind)}
                entityTypes={entityTypes}
                onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
              />
              {validationErrors.map((error) => (
                <Typography key={error} variant="caption" color="error">
                  {error}
                </Typography>
              ))}
            </PersistentTab>
          )}
          <PersistentTab<AssetViewTab> label="Source" value="source">
            <AssetSourceView content={content} isLoading={isLoading} />
          </PersistentTab>
          <PersistentTab<AssetViewTab> label="Fields" value="fields">
            <ScrollFadeContainer>
              <EntityFieldDisplayGroup entityType={entityType} fields={fields} />
            </ScrollFadeContainer>
          </PersistentTab>
        </PersistentTabs>
      </Stack>
    </PageLayout>
  )
}

export default AssetViewPage
